import React, { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { destroyScrollbars, initializeScrollbars } from './../../composables/scrollBar.js';

function cssVariable(name, fallback) {
	const value = getComputedStyle(document.body).getPropertyValue(name).trim();
	return value || fallback;
}

function chartColors() {
	return {
		primary: cssVariable('--bs-primary', '#348fe2'),
		dark: cssVariable('--bs-dark', '#212529'),
		blue: cssVariable('--bs-blue', '#348fe2'),
		teal: cssVariable('--bs-teal', '#00acac'),
		indigo: cssVariable('--bs-indigo', '#8753de'),
		orange: cssVariable('--bs-orange', '#f59c1a'),
		red: cssVariable('--bs-red', '#ff5b57'),
		lime: cssVariable('--bs-lime', '#90ca4b'),
		cyan: cssVariable('--bs-cyan', '#49b6d6'),
		green: cssVariable('--bs-green', '#198754'),
		gray: cssVariable('--bs-gray', '#6c757d'),
		gray200: cssVariable('--bs-gray-200', '#e9ecef'),
		gray400: cssVariable('--bs-gray-400', '#ced4da'),
		gray500: cssVariable('--bs-gray-500', '#adb5bd'),
		gray600: cssVariable('--bs-gray-600', '#6c757d'),
		body: cssVariable('--bs-body-color', '#6c757d'),
		border: cssVariable('--bs-border-color', 'rgba(0, 0, 0, .1)'),
		componentBg: cssVariable('--bs-component-bg', '#fff'),
		componentColor: cssVariable('--bs-component-color', '#212529'),
		componentColorRgb: cssVariable('--bs-component-color-rgb', '33, 37, 41'),
		primaryRgb: cssVariable('--bs-primary-rgb', '52, 143, 226'),
		white: cssVariable('--bs-white', '#fff')
	};
}

function renderChart(root, id, charts) {
	const container = root.querySelector('#' + id);
	const definition = charts[id];
	if (!container || !definition) return null;

	const baseOptions = definition.options || {
		chart: {
			type: definition.type,
			height: '100%',
			width: '100%',
			toolbar: { show: false },
			zoom: { enabled: false },
			animations: { enabled: true }
		},
		series: definition.series,
		colors: definition.colors,
		labels: definition.labels,
		dataLabels: { enabled: false },
		legend: definition.legend || { show: false },
		stroke: definition.stroke || { curve: 'smooth', width: 2 },
		fill: definition.fill || { opacity: 0.35 },
		grid: { borderColor: definition.gridColor || 'rgba(255,255,255,.15)' },
		xaxis: definition.xaxis || { labels: { style: { colors: '#8c96a0' } } },
		yaxis: definition.yaxis || { labels: { style: { colors: '#8c96a0' } } },
		plotOptions: definition.plotOptions,
		tooltip: { theme: 'dark' }
	};
	const chart = new ApexCharts(container, {
		...baseOptions,
		chart: {
			...(baseOptions.chart || {}),
			type: (baseOptions.chart && baseOptions.chart.type) || definition.type,
			...(definition.useDefaultChartDimensions === false
				? {}
				: {
						height: (baseOptions.chart && baseOptions.chart.height) || '100%',
						width: (baseOptions.chart && baseOptions.chart.width) || '100%'
					})
		},
		...(definition.useDefaultChartDimensions === false
			? {}
			: {
					plotOptions: { line: {}, ...(baseOptions.plotOptions || {}) }
				})
	});
	chart.render();
	return chart;
}

function renderMap(root, id, dark = false) {
	const map = root.querySelector('#' + id);
	if (!map) return null;

	if (map.__dashboardV2Map && map.__dashboardV2Map.destroy) {
		map.__dashboardV2Map.destroy();
	}
	map.innerHTML = '';

	const theme = cssVariable('--bs-app-theme', '#348fe2');
	const gray = cssVariable(dark ? '--bs-gray-700' : '--bs-gray-500', dark ? '#495057' : '#adb5bd');
	const background = dark ? cssVariable('--bs-gray-900', '#212529') : 'transparent';
	const mapInstance = new jsVectorMap({
		selector: '#' + id,
		map: 'world',
		zoomButtons: !dark,
		zoomOnScroll: false,
		hoverOpacity: 0.5,
		hoverColor: false,
		focusOn: { x: 0.5, y: 0.5, scale: 1 },
		markers: [
			{ name: 'Egypt', coords: [26.8206, 30.8025] },
			{ name: 'Russia', coords: [61.524, 105.3188] },
			{ name: 'Canada', coords: [56.1304, -106.3468] },
			{ name: 'Brazil', coords: [-14.235, -51.9253] }
		],
		markerStyle: { initial: { fill: theme, stroke: 'none', r: 5 }, hover: { fill: theme } },
		regionStyle: { initial: { fill: gray, fillOpacity: 0.5, stroke: 'none' }, hover: { fillOpacity: 0.8 } },
		backgroundColor: background
	});
	const destroy = () => {
		mapInstance.destroy();
		if (map.__dashboardV2Map && map.__dashboardV2Map.instance === mapInstance) {
			delete map.__dashboardV2Map;
			map.innerHTML = '';
		}
	};
	map.__dashboardV2Map = { destroy, instance: mapInstance };
	return { destroy };
}

function animateReferenceNumbers(root) {
	root.querySelectorAll('[data-animation="number"][data-value]').forEach((element) => {
		const value = Number(element.getAttribute('data-value'));
		if (!Number.isNaN(value)) {
			element.textContent = value.toLocaleString(undefined, {
				minimumFractionDigits: value % 1 ? 2 : 0,
				maximumFractionDigits: 2
			});
		}
	});
}

function animateReferenceProgress(root) {
	root.querySelectorAll('[data-animation="width"][data-value]').forEach((element) => {
		const value = element.getAttribute('data-value');
		const numericValue = value.replace('%', '');
		element.setAttribute('role', 'progressbar');
		element.setAttribute('aria-valuemin', '0');
		element.setAttribute('aria-valuemax', '100');
		element.setAttribute('aria-valuenow', numericValue);
		element.style.width = '0%';
		const applyWidth = () => {
			element.style.width = value;
		};
		window.requestAnimationFrame(applyWidth);
		window.setTimeout(applyWidth, 50);
	});
}

function getCharts(colors, root) {
	const offsets = Array.from({ length: 77 }, (_, index) => (index === 3 ? 73 : 77 - index));
	const pageOffsets = offsets.slice(0, -2).concat([2, 0]);
	const dateSeries = (values, seriesOffsets = offsets) =>
		values.map((value, index) => {
			const date = new Date();
			date.setDate(date.getDate() - seriesOffsets[index]);
			return [date.getTime(), value];
		});
	const uniqueVisitors = [
		13, 13, 6, 6, 6, 5, 5, 5, 6, 7, 6, 9, 9, 8, 10, 10, 10, 10, 9, 9, 10, 9, 9, 8, 8, 8, 8, 8, 7, 7, 6, 6, 6, 6, 5,
		5, 4, 4, 5, 5, 5, 7, 7, 7, 10, 9, 9, 10, 11, 11, 8, 8, 7, 8, 9, 8, 9, 10, 9, 10, 16, 17, 16, 17, 16, 15, 14, 24,
		18, 15, 14, 16, 16, 17, 7, 7, 7
	];
	const pageViews = [
		14, 13, 15, 14, 13, 15, 16, 16, 14, 14, 13, 12, 13, 13, 15, 16, 16, 17, 17, 18, 15, 15, 15, 19, 19, 18, 18, 17,
		16, 18, 18, 18, 16, 14, 14, 13, 14, 13, 10, 9, 10, 11, 11, 11, 10, 9, 10, 13, 14, 14, 13, 12, 11, 13, 13, 13,
		13, 14, 13, 13, 19, 21, 22, 25, 24, 24, 22, 16, 15, 12, 12, 15, 15, 15, 18, 18, 17
	];
	const visitorSeries = [
		{ name: 'Unique Visitors', data: dateSeries(uniqueVisitors) },
		{ name: 'Page Views', data: dateSeries(pageViews, pageOffsets) }
	];
	const charts = {};

	charts.visitorLineChart = {
		type: 'area',
		options: {
			series: visitorSeries,
			colors: [colors.teal, colors.blue],
			fill: { opacity: 0.75, type: 'solid' },
			legend: {
				position: 'top',
				floating: false,
				horizontalAlign: 'right',
				offsetY: 15,
				offsetX: 500,
				labels: { colors: colors.white }
			},
			xaxis: { type: 'datetime', tickAmount: 6, labels: { style: { colors: colors.white } } },
			yaxis: { labels: { style: { colors: colors.white } } },
			tooltip: { y: { formatter: (value) => '$ ' + value + ' thousands' } },
			chart: { height: 270, type: 'area', toolbar: { show: false }, stacked: true, zoom: { enabled: false } },
			plotOptions: { bar: { horizontal: false, columnWidth: '55%', endingShape: 'rounded' } },
			dataLabels: { enabled: false },
			grid: {
				show: true,
				borderColor: 'rgba(255,255,255, .15)',
				xaxis: { lines: { show: true } },
				yaxis: { lines: { show: true } },
				padding: { top: -40, right: 3, bottom: 0, left: 10 }
			},
			stroke: { show: false, curve: 'straight' }
		}
	};
	charts.visitorDonutChart = {
		type: 'donut',
		useDefaultChartDimensions: false,
		options: {
			series: [416747, 784466],
			labels: ['New Visitors', 'Return Visitors'],
			chart: { type: 'donut' },
			dataLabels: { dropShadow: { enabled: false }, style: { colors: ['#fff'] } },
			stroke: { show: false },
			colors: [colors.teal, colors.blue],
			legend: { show: false }
		}
	};

	return charts;
}

function wireActions(root) {
	root.querySelectorAll('a[href="#/"]').forEach((link) => {
		link.addEventListener('click', (event) => event.preventDefault());
	});

	const exportButton = root.querySelector('#dashboard-export');
	if (exportButton) {
		exportButton.addEventListener('click', (event) => {
			event.preventDefault();
			const csv = 'Metric,Value\nTotal Sales,128940.55\nOrders,3824\nConversion,5.12%\n';
			const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
			const link = document.createElement('a');
			link.href = url;
			link.download = 'dashboard-export.csv';
			link.click();
			URL.revokeObjectURL(url);
		});
	}

}

function DashboardV2() {
	const rootRef = useRef(null);
	const [themeVersion, setThemeVersion] = useState(0);
	const [calendarDate, setCalendarDate] = useState(() => new Date());

	useEffect(() => {
		if (!rootRef.current) return undefined;
		const root = rootRef.current;
		const colors = chartColors();
		const charts = [];
		const maps = [renderMap(root, 'visitorMap', true)];
		const definitions = getCharts(colors, root);

		animateReferenceNumbers(root);
		animateReferenceProgress(root);
		Object.keys(definitions).forEach((id) => {
			const chart = renderChart(root, id, definitions);
			if (chart) charts.push(chart);
		});
		const scrollbars = initializeScrollbars(root);
		wireActions(root);

		const refresh = () => setThemeVersion((current) => current + 1);
		document.addEventListener('theme-reload', refresh);
		return () => {
			document.removeEventListener('theme-reload', refresh);
			charts.forEach((chart) => chart.destroy());
			maps.filter(Boolean).forEach((map) => map.destroy && map.destroy());
		destroyScrollbars(scrollbars);
		};
	}, [themeVersion]);

	return (
		<div ref={rootRef}>
			<ol className="breadcrumb float-xl-end">
				<li className="breadcrumb-item">
					<a href="#/">Home</a>
				</li>
				<li className="breadcrumb-item">
					<a href="#/">Dashboard</a>
				</li>
				<li className="breadcrumb-item active">Dashboard v2</li>
			</ol>
			<h1 className="page-header">
				Dashboard v2 <small>page header description goes here...</small>
			</h1>
			<div className="row mb-3">
				<div className="col-xl-3 col-md-6">
					<div
						className="card rounded-3 border-0 bg-gradient-135 bg-gradient-from-info bg-gradient-to-blue overflow-hidden"
						data-bs-theme="dark"
					>
						<div className="card-body position-relative z-3">
							<div className="mb-2 fw-bold text-white">Today's Visits</div>
							<div className="h2 mb-4">7,842,900</div>
							<div className="progress h-5px bg-black mb-2">
								<div
									className="progress-bar bg-white bg-opacity-100 rounded-end"
									style={{ width: '70.1%' }}
								></div>
							</div>
							<div className="small fw-semibold text-white text-opacity-75 mb-n1">
								Better than last week (70.1%)
							</div>
						</div>
						<div className="position-absolute w-100px h-100px bottom-0 end-0 d-flex align-items-center justify-content-center m-n3">
							<iconify-icon
								icon="solar:mouse-circle-bold-duotone"
								className="text-black text-opacity-30"
								style={{ fontSize: '150px' }}
							></iconify-icon>
						</div>
					</div>
				</div>
				<div className="col-xl-3 col-md-6 mt-3 mt-md-0">
					<div
						className="card rounded-3 border-0 bg-gradient-135 bg-gradient-from-indigo bg-gradient-to-purple overflow-hidden"
						data-bs-theme="dark"
					>
						<div className="card-body position-relative z-3">
							<div className="mb-2 fw-bold text-white">Today's Profit</div>
							<div className="h2 mb-4">180,200</div>
							<div className="progress h-5px bg-black mb-2">
								<div
									className="progress-bar bg-white bg-opacity-100 rounded-end"
									style={{ width: '40.5%' }}
								></div>
							</div>
							<div className="small fw-semibold text-white text-opacity-75 mb-n1">
								Better than last week (40.5%)
							</div>
						</div>
						<div className="position-absolute w-100px h-100px bottom-0 end-0 d-flex align-items-center justify-content-center m-n3">
							<iconify-icon
								icon="solar:dollar-bold-duotone"
								className="text-black text-opacity-30"
								style={{ fontSize: '150px' }}
							></iconify-icon>
						</div>
					</div>
				</div>
				<div className="col-xl-3 col-md-6 mt-3 mt-xl-0">
					<div
						className="card rounded-3 border-0 bg-gradient-135 bg-gradient-from-orange bg-gradient-to-pink overflow-hidden"
						data-bs-theme="dark"
					>
						<div className="card-body position-relative z-3">
							<div className="mb-2 fw-bold text-white">New Orders</div>
							<div className="h2 mb-4">38,900</div>
							<div className="progress h-5px bg-black mb-2">
								<div
									className="progress-bar bg-white bg-opacity-100 rounded-end"
									style={{ width: '76.3%' }}
								></div>
							</div>
							<div className="small fw-semibold text-white text-opacity-75 mb-n1">
								Better than last week (76.3%)
							</div>
						</div>
						<div className="position-absolute w-100px h-100px bottom-0 end-0 d-flex align-items-center justify-content-center m-n3">
							<iconify-icon
								icon="solar:bag-smile-bold-duotone"
								className="text-black text-opacity-30"
								style={{ fontSize: '150px' }}
							></iconify-icon>
						</div>
					</div>
				</div>
				<div className="col-xl-3 col-md-6 mt-3 mt-xl-0">
					<div
						className="card rounded-3 border-0 bg-gradient-135 bg-gradient-from-gray-600 bg-gradient-to-gray-800 overflow-hidden"
						data-bs-theme="dark"
					>
						<div className="card-body position-relative z-3">
							<div className="mb-2 fw-bold text-white">New Comments</div>
							<div className="h2 mb-4">3,988</div>
							<div className="progress h-5px bg-black mb-2">
								<div
									className="progress-bar bg-white bg-opacity-100 rounded-end"
									style={{ width: '54.9%' }}
								></div>
							</div>
							<div className="small fw-semibold text-white text-opacity-75 mb-n1">
								Better than last week (54.9%)
							</div>
						</div>
						<div className="position-absolute w-100px h-100px bottom-0 end-0 d-flex align-items-center justify-content-center m-n3">
							<iconify-icon
								icon="solar:chat-dots-bold-duotone"
								className="text-black text-opacity-30"
								style={{ fontSize: '150px' }}
							></iconify-icon>
						</div>
					</div>
				</div>
			</div>
			<div className="row gy-3 mb-3">
				<div className="col-xl-8">
					<div
						className="card border-0 rounded-3 d-sm-flex flex-sm-row overflow-hidden h-100"
						data-bs-theme="dark"
					>
						<div className="p-3 flex-1">
							<div className="fs-5 fw-bold mb-0 text-white">Visitors Analytics</div>
							<div className="fw-semibold small text-body text-opacity-75 mb-3">
								Where do our visitors come from
							</div>
							<div className="ms-n3 mb-n2">
								<div id="visitorLineChart" style={{ height: '270px' }}></div>
							</div>
						</div>
						<div className="p-3 bg-gray-900 w-sm-250px d-flex flex-column">
							<div>
								<div className="h3 mb-0 lh-base ">1,225,729</div>
								<div className="fw-semibold small text-body text-opacity-75">Total visitors</div>
							</div>
							<div
								id="visitorDonutChart"
								className="w-200px py-3 flex-1 d-flex align-items-center mx-auto"
							></div>
							<div className="d-flex flex-column gap-1 small">
								<div className="d-flex gap-2 align-items-center">
									<div className="w-8px h-8px rounded bg-teal"></div>
									<div className="fw-semibold text-white w-45px ps-1">34.7%</div>
									<div className="fw-semibold text-body text-opacity-50">New Visitors</div>
								</div>
								<div className="d-flex gap-2 align-items-center">
									<div className="w-8px h-8px rounded bg-primary"></div>
									<div className="fw-semibold text-white w-45px ps-1">65.3%</div>
									<div className="fw-semibold text-body text-opacity-50">Return Visitors</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-4">
					<div className="card border-0 rounded-3 h-100 overflow-hidden" data-bs-theme="dark">
						<div className="card-header fw-bold border-0">Visitors Origin</div>
						<div id="visitorMap" className="bg-gray-900" style={{ height: '200px' }}></div>
						<div className="list-group list-group-flush">
							<a
								href="#/"
								className="list-group-item list-group-item-action d-flex fw-semibold gap-3"
							>
								<span className="flex-1">United State</span>
								<span className="badge bg-teal d-flex align-items-center">20.95%</span>
							</a>
							<a
								href="#/"
								className="list-group-item list-group-item-action d-flex fw-semibold gap-3"
							>
								<span className="flex-1">India</span>
								<span className="badge bg-primary d-flex align-items-center">16.12%</span>
							</a>
							<a
								href="#/"
								className="list-group-item list-group-item-action d-flex fw-semibold gap-3"
							>
								<span className="flex-1">Mongolia</span>
								<span className="badge bg-gray-700 d-flex align-items-center">14.99%</span>
							</a>
						</div>
					</div>
				</div>
			</div>
			<div className="row gy-3">
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 rounded-3 h-100">
						<div className="card-header fw-bold border-0 bg-gray-900 text-white" data-bs-theme="dark">
							<span className="flex-1">Chat History</span>
							<span className="badge bg-teal rounded-2 p-2 my-n1">4 message</span>
						</div>
						<div className="card-body bg-light" data-scrollbar="true" data-height="332px">
							<div className="widget-chat">
								<div className="widget-chat-item">
									<div className="widget-chat-media">
										<img src="/color-admin/admin/react/assets/img/user/profile.jpg" alt="" />
									</div>
									<div className="widget-chat-content">
										<div className="widget-chat-name">Roberto Lambert</div>
										<div className="widget-chat-message last">
											Hey, I'm testing out group messaging.
										</div>
									</div>
								</div>
								<div className="widget-chat-item reply">
									<div className="widget-chat-content">
										<div className="widget-chat-message last">Cool</div>
										<div className="widget-chat-status">
											<b>Read</b> 16:26
										</div>
									</div>
								</div>
								<div className="widget-chat-date">Today 14:21</div>
								<div className="widget-chat-item">
									<div className="widget-chat-media">
										<img src="/color-admin/admin/react/assets/img/user/profile.jpg" alt="" />
									</div>
									<div className="widget-chat-content">
										<div className="widget-chat-name">Rick Powell</div>
										<div className="widget-chat-message last">Awesome! What's new?</div>
									</div>
								</div>
								<div className="widget-chat-item">
									<div className="widget-chat-media">
										<img src="/color-admin/admin/react/assets/img/user/profile.jpg" alt="" />
									</div>
									<div className="widget-chat-content">
										<div className="widget-chat-name">Roberto Lambert</div>
										<div className="widget-chat-message">
											Not much, It's got a new look, contact pics show up in group messaging, some
											other small stuff.
										</div>
										<div className="widget-chat-message last">
											How's crusty old iOS 6 treating you?
										</div>
									</div>
								</div>
								<div className="widget-chat-item reply">
									<div className="widget-chat-content">
										<div className="widget-chat-message last">Sucks</div>
										<div className="widget-chat-status">
											<b>Read</b> 16:30
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="card-footer bg-none d-flex gap-2">
<img
								src="/color-admin/admin/react/assets/img/user/profile.jpg"
								width="37px"
								height="37px"
								className="object-fit-cover rounded-pill"
							 alt="" />
							<div className="input-group">
								<input
									type="text"
									className="form-control border-end-0 rounded-pill rounded-end-0 ps-3"
									placeholder="Write something..."
								/>
								<div className="input-group-text bg-none py-3px px-4px rounded-pill rounded-start-0">
									<button
										className="btn btn-primary w-80px btn-sm rounded-pill py-4px px-3"
										type="button"
									>
										Send
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 rounded-3 h-100">
						<div className="card-header fw-bold border-0 bg-gray-900 text-white" data-bs-theme="dark">
							<span className="flex-1">Today's Schedule</span>
						</div>
							<div className="p-1">
								<div id="calendar">
									<DatePicker
										inline
										selected={calendarDate}
										onChange={setCalendarDate}
										dateFormat="yyyy-MM-dd"
									/>
								</div>
						</div>
						<hr className="m-0 bg-gray-500" />
						<div className="list-group list-group-flush">
							<a
								href="#/"
								className="list-group-item list-group-item-action fw-semibold d-flex"
							>
								<span className="flex-1">Sales Reporting</span>
								<span className="badge bg-teal d-flex align-items-center">9:00 am</span>
							</a>
							<a
								href="#/"
								className="list-group-item list-group-item-action fw-semibold d-flex"
							>
								<span className="flex-1">Have a meeting with sales team</span>
								<span className="badge bg-primary d-flex align-items-center">2:45 pm</span>
							</a>
							<a
								href="#/"
								className="list-group-item list-group-item-action fw-semibold d-flex rounded-bottom"
							>
								<span className="flex-1">Review quarterly report</span>
								<span className="badge bg-indigo d-flex align-items-center">4:30 pm</span>
							</a>
						</div>
					</div>
				</div>
				<div className="col-xl-4 col-lg-6">
					<div className="card border-0 rounded-3 h-100">
						<div className="card-header fw-bold border-0 bg-gray-900 text-white" data-bs-theme="dark">
							<span className="flex-1">New Registered Users</span>
							<span className="badge bg-teal rounded-2 p-2 my-n1">24 new users</span>
						</div>
						<div className="card-body">
							<div className="row gy-3 text-center">
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-1.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Azure Breeze</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">
										Portuguese
									</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-2.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Crimson Tide</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Moroccan</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-3.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Velvet Thunder</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Egyptian</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-4.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Silent Oak</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Chilean</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-5.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Savory Posh</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Algerian</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-6.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Ancient Caviar</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Korean</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-7.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Marble Lungs</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Indian</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-8.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Blank Bloke</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Japanese</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-9.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Hip Sculling</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Cuban</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-10.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Flat Moon</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Nepalese</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-11.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Packed Puffs</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">
										Malaysian
									</div>
								</div>
								<div className="col-3">
									<div className="mb-2 d-flex align-items-center justify-content-center">
										<img
											src="/color-admin/admin/react/assets/img/user/user-12.jpg"
											alt=""
											width="55"
											height="55"
											className="rounded-circle"
										/>
									</div>
									<div className="small fw-bold text-ellipsis">Clay Hike</div>
									<div className="small fw-semibold text-ellipsis text-secondary lh-sm">Swedish</div>
								</div>
							</div>
						</div>
						<div className="card-footer text-center bg-none py-2px">
							<a
								href="#/"
								className="text-decoration-none link-dark fw-bold small py-10px d-block"
							>
								View All
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default DashboardV2;
